import { GATHER_TIERS, INFRA } from '../../data/farm';
import { buyGather, buyInfra, upgradeNest, upgradeNestsMax } from '../../core/actions';
import { describeInfra } from '../../core/describe';
import { farmTotals, infraCost, nestCapacity, nestUpgradeCost } from '../../core/economy';
import { formatMoney } from '../../core/format';
import { firstUnmet, reqText, reqsMet } from '../../core/requirements';
import { game } from '../controller';
import { h, type Bindings } from '../dom';
import { buyButton, card, purchase, section, tag, type Panel } from '../shop';

const GATHER_ICON = ['🧺', '🥚', '🧺', '👜', '🧑‍🌾', '➡️', '🛝', '💨', '🏭', '🏠', '🤖', '🧠', '🚀'];
const INFRA_ICON: Record<string, string> = { scratchFeeder: '🌾', waterer: '💧', eggInspector: '🔍', warmingLamp: '💡' };

function cheapestNest(flockId: number): { index: number; cost: number } | null {
  const flock = game.state.flocks.find((f) => f.id === flockId);
  let best: { index: number; cost: number } | null = null;
  flock?.boxes.forEach((b, i) => {
    const c = nestUpgradeCost(b);
    if (c !== null && (!best || c < best.cost)) best = { index: i, cost: c };
  });
  return best;
}

function maxPreview(flockId: number): { count: number; cost: number } {
  const flock = game.state.flocks.find((f) => f.id === flockId);
  if (!flock) return { count: 0, cost: 0 };
  const levels = flock.boxes.map((b) => b.capacityLevel);
  let money = game.state.money;
  let count = 0;
  let cost = 0;
  for (;;) {
    let best: { i: number; c: number } | null = null;
    levels.forEach((lvl, i) => {
      const c = nestUpgradeCost({ capacityLevel: lvl, eggs: 0, hen: null, oldestEggAt: null });
      if (c !== null && (!best || c < best.c)) best = { i, c };
    });
    const pick = best as { i: number; c: number } | null;
    if (!pick || money < pick.c) break;
    money -= pick.c;
    cost += pick.c;
    levels[pick.i]!++;
    count++;
  }
  return { count, cost };
}

export const farmPanel: Panel = {
  id: 'farm',
  label: 'Farm',
  badge: () => {
    const s = game.state;
    const next = GATHER_TIERS.find((g) => (s.gather[g.id] ?? 0) < g.maxOwned && (!g.postWin || s.won));
    return next && s.money >= next.cost && !Object.keys(s.gather).length ? 1 : 0;
  },
  key: () => {
    const s = game.state;
    return [s.viewFlock, JSON.stringify(s.gather), JSON.stringify(s.infra), s.won, game.viewFlock().boxes.length, s.upgrades.length].join('|');
  },
  build(root: HTMLElement, b: Bindings) {
    const s = game.state;
    const flock = game.viewFlock();
    const summary = h('div', { class: 'card' });
    b.add(() => {
      const t = farmTotals(game.state, game.ctx());
      summary.textContent = t.gatherRate > 0
        ? `Hens lay ${t.layRate.toFixed(1)} eggs/s. Gatherers collect ${t.gatherRate.toFixed(1)} eggs/s (${Math.round(t.efficiency * 100)}%). Eggs lost to full nests: ${game.state.stats.eggsLost.toLocaleString('en-US')}.`
        : `Hens lay ${t.layRate.toFixed(1)} eggs/s, but nobody gathers them while you're not clicking — and you earn nothing offline. Buy a basket!`;
    });
    root.append(summary);

    const gather = section('🧺 Gathering', 'auto-collects eggs, even while you are away');
    const nextIdx = GATHER_TIERS.findIndex((g) => (s.gather[g.id] ?? 0) < g.maxOwned);
    GATHER_TIERS.forEach((g, i) => {
      const ownedN = s.gather[g.id] ?? 0;
      if (i > nextIdx + 1 && ownedN === 0) return;
      const done = ownedN >= g.maxOwned;
      const lockedPostWin = g.postWin && !s.won;
      const buy = buyButton({ cost: () => g.cost, enabled: () => !done && !lockedPostWin, onBuy: () => purchase(() => buyGather(game.state, g.id)) }, b);
      const desc = `+${g.rate} eggs/s${g.maxOwned > 1 ? ` each (${ownedN}/${g.maxOwned})` : ''}${g.valueBonus > 1 ? ` · gathered eggs worth ×${g.valueBonus}` : ''}`;
      gather.append(card({
        cls: done ? 'owned' : lockedPostWin ? 'locked' : '',
        icon: GATHER_ICON[i] ?? '🧺',
        title: [g.name, done ? tag('owned') : lockedPostWin ? tag('after Stage 1') : null].filter(Boolean) as (Node | string)[],
        desc,
        flavor: g.flavor,
        side: done ? [] : [buy.price, buy.btn],
      }));
    });
    root.append(gather);

    const infra = section('🏗️ Infrastructure', 'farm-wide');
    for (const def of INFRA) {
      const n = s.infra[def.id] ?? 0;
      const unlocked = reqsMet(s, def.requires);
      const req = firstUnmet(s, def.requires);
      const maxed = n >= def.maxOwned;
      const buy = buyButton({ cost: () => infraCost(game.state, def), enabled: () => !maxed && unlocked, onBuy: () => purchase(() => buyInfra(game.state, def.id)) }, b);
      infra.append(card({
        cls: !unlocked ? 'locked' : maxed ? 'owned' : '',
        icon: INFRA_ICON[def.id] ?? '🏗️',
        title: [def.name, tag(`${n}/${def.maxOwned}`)],
        desc: describeInfra(def),
        meta: !unlocked && req ? `Needs: ${reqText(s, req)}` : '',
        flavor: def.flavor,
        side: maxed ? [] : [buy.price, buy.btn],
      }));
    }
    root.append(infra);

    const nests = section('🪺 Nest capacity', flock.name);
    const capLine = h('div', { class: 'desc' });
    const nextBtn = h('button', { class: 'btn small', type: 'button', onclick: () => {
      const c = cheapestNest(flock.id);
      if (c) purchase(() => upgradeNest(game.state, flock.id, c.index));
    } });
    const maxBtn = h('button', { class: 'btn small secondary', type: 'button', onclick: () => purchase(() => upgradeNestsMax(game.state, flock.id)) });
    b.add(() => {
      const f = game.viewFlock();
      const caps = f.boxes.map(nestCapacity);
      const avg = caps.length ? caps.reduce((a, x) => a + x, 0) / caps.length : 0;
      capLine.textContent = `Nests hold ${avg.toFixed(1)} eggs on average (max 24). Full nests waste eggs — bigger nests buffer between visits.`;
      const c = cheapestNest(f.id);
      nextBtn.textContent = c ? `Buy next ${formatMoney(c.cost)}` : 'All maxed';
      nextBtn.disabled = !c || game.state.money < c.cost;
      const m = maxPreview(f.id);
      maxBtn.textContent = m.count ? `Buy max (${m.count}× ${formatMoney(m.cost)})` : 'Buy max';
      maxBtn.disabled = m.count === 0;
    });
    nests.append(card({ icon: '🪺', title: ['Bigger nests'], desc: capLine, side: [nextBtn, maxBtn] }));
    root.append(nests);
  },
};
